import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiVizSpec,
  BiVizStyle,
  BiVizType,
  biVizHasRightAxis,
  kBiVizStyleDefaults,
  kBiVizStyleOptionNames,
} from '@euler/model/bi/viz';
import { memo } from 'react';
import { StyleGroup } from '../StyleGroup';
import { biWithStyleOption } from '../functions/biStyleEdit';
import { BiStyleOptionProps } from '../functions/biStyleOptionProps';
import { biStyleProblem } from '../functions/biStyleProblem';
import { CategoryAxisFields } from './CategoryAxisFields';
import { ValueAxisFields } from './ValueAxisFields';

type Axes = NonNullable<BiVizStyle['axes']>;
type Side = keyof Axes;

/** An object without its undefined fields, as the wire carries it. */
function defined<T extends object>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, field]) => field !== undefined),
  ) as T;
}

/** The category axis, the value axis, and the right one when used. */
export const AxesOptions = memo(
  ({ style, viz, onChange }: BiStyleOptionProps & { viz: BiVizSpec }) => {
    const { rpx } = useResponsiveContext();
    const axes: Axes = style?.axes ?? {};
    const problem = biStyleProblem(style);
    const current = <S extends Side>(side: S) =>
      ({ ...kBiVizStyleDefaults.axes[side], ...axes[side] }) as NonNullable<
        Axes[S]
      >;
    const set = <S extends Side>(
      side: S,
      patch: Partial<NonNullable<Axes[S]>>,
    ) =>
      onChange(
        biWithStyleOption(style, 'axes', {
          ...axes,
          [side]: defined({ ...current(side), ...patch }),
        }),
      );
    const horizontal = viz.type === BiVizType.HorizontalBar;
    return (
      <StyleGroup
        title={kBiVizStyleOptionNames.axes}
        onReset={
          style?.axes
            ? () => onChange(biWithStyleOption(style, 'axes', undefined))
            : null
        }
      >
        <CategoryAxisFields
          name={horizontal ? '分类轴' : '横轴'}
          axis={current('x')}
          canRotate={!horizontal}
          onChange={patch => set('x', patch)}
        />
        <ValueAxisFields
          name={
            horizontal ? '数值轴' : biVizHasRightAxis(viz) ? '左轴' : '纵轴'
          }
          axis={current('y')}
          onChange={patch => set('y', patch)}
        />
        {biVizHasRightAxis(viz) ? (
          <ValueAxisFields
            name="右轴"
            axis={current('y2')}
            onChange={patch => set('y2', patch)}
          />
        ) : null}
        {problem ? (
          <Typography.Text size={rpx(12)} color={aiColors.destructive}>
            {problem}
          </Typography.Text>
        ) : null}
      </StyleGroup>
    );
  },
);
